/** Akkorder som data: bokstavakkorder (Am7/G), tall-akkorder (6m/1) og pause (N.C.). Rene funksjoner. */
import { tonenavn } from './toneart';
import type { Toneart } from './toneart';

export type Fortegn = '' | '#' | 'b';
export type Rot =
  | { type: 'bokstav'; bokstav: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H'; fortegn: Fortegn }
  | { type: 'trinn'; trinn: 1 | 2 | 3 | 4 | 5 | 6 | 7; fortegn: Fortegn };
export type Akkord =
  | { type: 'pause' }
  | { type: 'akkord'; rot: Rot; kvalitet: string; bass: Rot | null };

const PAUSE = 'N.C.';

// 6/9 prøves før vanlig tall.
const NOTE = '([A-G][b#]?|H)';
const BOKSTAV_KVALITET =
  '(?:maj|min|dim|aug|sus|add|m|M)?(?:6/9|\\d*)(?:(?:sus|add|maj|[#b+-])\\d+)*(?:\\((?:add|sus|[#b])?\\d+\\))*';
const BOKSTAVAKKORD = new RegExp(`^${NOTE}(${BOKSTAV_KVALITET})(?:/${NOTE})?$`);

const TALL_KVALITET = '(?:maj|min|dim|aug|sus|add|m|M|°)\\d*(?:[b#]\\d+)*';
const TALLAKKORD = new RegExp(`^([b#]?)([1-7])((?:${TALL_KVALITET})?)(?:/([b#]?)([1-7]))?$`);
const TAKTART = /^\d\/[48]$/;

const GRUNNTONE: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11, H: 11 };
const HALVTONER: Record<Fortegn, number> = { '': 0, '#': 1, b: -1 };
const DUR_SKALA = [0, 2, 4, 5, 7, 9, 11];
const MOLL_SKALA = [0, 2, 3, 5, 7, 8, 10];

function bokstavrot(note: string): Rot {
  return {
    type: 'bokstav',
    bokstav: note[0] as 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H',
    fortegn: note.slice(1) as Fortegn,
  };
}

function trinnrot(fortegn: string, trinn: string): Rot {
  return { type: 'trinn', trinn: Number(trinn) as 1 | 2 | 3 | 4 | 5 | 6 | 7, fortegn: fortegn as Fortegn };
}

/** Tolker ett token til en akkord. Alt annet enn en akkord gir null. ♯ og ♭ leses som # og b. */
export function tolkAkkord(token: string): Akkord | null {
  const tekst = token.replace(/♯/g, '#').replace(/♭/g, 'b');
  if (tekst === PAUSE) return { type: 'pause' };

  const bokstav = BOKSTAVAKKORD.exec(tekst);
  if (bokstav) {
    return { type: 'akkord', rot: bokstavrot(bokstav[1]), kvalitet: bokstav[2], bass: bokstav[3] ? bokstavrot(bokstav[3]) : null };
  }

  const tall = TALLAKKORD.exec(tekst);
  if (!tall || TAKTART.test(tekst)) return null;
  return { type: 'akkord', rot: trinnrot(tall[1], tall[2]), kvalitet: tall[3], bass: tall[5] ? trinnrot(tall[4], tall[5]) : null };
}

/** Sann for bokstavakkorder og N.C., ikke for tall-akkorder. */
export function erBokstavakkord(token: string): boolean {
  const akkord = tolkAkkord(token);
  return akkord !== null && (akkord.type === 'pause' || akkord.rot.type === 'bokstav');
}

/** Sann bare for tall-akkorder. */
export function erTallakkord(token: string): boolean {
  const akkord = tolkAkkord(token);
  return akkord !== null && akkord.type === 'akkord' && akkord.rot.type === 'trinn';
}

function skrivRot(rot: Rot): string {
  return rot.type === 'bokstav' ? `${rot.bokstav}${rot.fortegn}` : `${rot.fortegn}${rot.trinn}`;
}

/** Skriver en akkord som tekst, alltid med # og b. */
export function skrivAkkord(akkord: Akkord): string {
  if (akkord.type === 'pause') return PAUSE;
  const bass = akkord.bass ? `/${skrivRot(akkord.bass)}` : '';
  return `${skrivRot(akkord.rot)}${akkord.kvalitet}${bass}`;
}

function byttRotOgBass(akkord: Akkord, bytt: (rot: Rot) => Rot): Akkord {
  if (akkord.type === 'pause') return akkord;
  return { ...akkord, rot: bytt(akkord.rot), bass: akkord.bass && bytt(akkord.bass) };
}

/** Bokstavrot for en tone. Tone 11 skrives H bare med `h`. */
function roten(tone: number, flatt: boolean, h?: boolean): Rot {
  const navn = tonenavn(tone, flatt);
  return bokstavrot(h && navn === 'B' ? 'H' : navn);
}

/**
 * Flytter rot og bass i bokstavakkorder, med kryss uten `flatt`. Kvalitet, pause og tall er urørt.
 * 0 semitoner beholder stavemåten, slik at en sang i originaltoneart ser ut som den ble skrevet.
 */
export function transponerAkkord(akkord: Akkord, semitoner: number, valg: { flatt?: boolean; h?: boolean } = {}): Akkord {
  if (semitoner === 0) return akkord;
  const { flatt = false, h } = valg;
  return byttRotOgBass(akkord, (rot) =>
    rot.type === 'bokstav' ? roten(GRUNNTONE[rot.bokstav] + HALVTONER[rot.fortegn] + semitoner, flatt, h) : rot
  );
}

/** Trinn uten fortegn følger skalaen i tonearten. Trinn med fortegn regnes fra durskalaen på samme grunntone. */
function trinnTilBokstav(rot: Extract<Rot, { type: 'trinn' }>, toneart: Toneart, h?: boolean): Rot {
  const skala = toneart.slekt === 'moll' && rot.fortegn === '' ? MOLL_SKALA : DUR_SKALA;
  const flatt = rot.fortegn === 'b' || (rot.fortegn === '' && toneart.flatt);
  return roten(toneart.rot + skala[rot.trinn - 1] + HALVTONER[rot.fortegn], flatt, h);
}

/** Skriver tall-akkorder som bokstaver i tonearten. Fortegnet på trinnet velger stavemåte, ellers `toneart.flatt`. */
export function tilBokstaver(akkord: Akkord, toneart: Toneart, valg: { h?: boolean } = {}): Akkord {
  return byttRotOgBass(akkord, (rot) => (rot.type === 'trinn' ? trinnTilBokstav(rot, toneart, valg.h) : rot));
}
